<template>
  <div v-if="sankeyThemesSet.size">
    <div class="pa-4 pt-0">
      <ThresholdControls />
      <PickedWordsControls />
    </div>
    <svg :viewBox="`${-width / 2} ${-height / 2} ${width} ${height}`">
      <!-- Moods -->
      <g :transform="`translate(${pullInDistance},0)`">
        <!-- Sources -->
        <g>
          <rect
            v-for="s in themesMoodsLayout.sourceGroups"
            :key="`theme-${s.key}`"
            :x="s.x0"
            :width="Math.abs(s.x1 - s.x0)"
            :y="s.y0"
            :height="s.y1 - s.y0"
            :fill="color(s.key)"
            :fill-opacity="getFillOpacity({ theme: s.key })"
          ></rect>
        </g>
        <!-- Links -->
        <g>
          <path
            v-for="l in themesMoodsLayout.links"
            :key="`theme-${l.source.key}-mood-${l.target.key}`"
            :d="ribbon(l)"
            :fill="color(l.source.key)"
            :fill-opacity="
              getFillOpacity({ theme: l.source.key, mood: l.target.key }) * 0.3
            "
          ></path>
        </g>
        <!-- Targets -->
        <g>
          <g
            v-for="t in themesMoodsLayout.targetGroups"
            :key="`mood-${t.key}`"
            :transform="`translate(0,${(t.y0 + t.y1) / 2})`"
          >
            <rect
              v-for="d in t.stacked"
              :key="`theme-${d.key}`"
              :fill="color(d.key)"
              :y="-d.bandwidth / 2"
              :height="d.bandwidth"
              :x="Math.min(d[0], d[1])"
              :width="Math.abs(d[0] - d[1])"
              :fill-opacity="getFillOpacity({ theme: d.key, mood: t.key })"
              style="cursor: pointer"
              @mouseenter="onMouseEnterThemeMood(d, t)"
              @mouseleave="onMouseLeaveThemeMood(d, t)"
              @click="onMouseClickThemeMood(d, t)"
            ></rect>
          </g>
        </g>
        <g v-for="t in themesMoodsLayout.targetGroups" :key="`motif-${t.key}`">
          <polyline
            :points="getPolylinePoints(t)"
            fill="none"
            stroke="currentColor"
            :stroke-opacity="getFillOpacity({ mood: t.key })"
          ></polyline>
          <text
            dy="0.31em"
            fill="currentColor"
            :transform="`translate(${getlabelPoint(t)})`"
            text-anchor="end"
            :fill-opacity="getFillOpacity({ mood: t.key })"
            style="cursor: pointer"
            @mouseenter="onMouseEnterMood(t)"
            @mouseleave="onMouseLeaveMood(t)"
            @click="onMouseClickMood(t)"
          >
            {{ t.key }}
          </text>
        </g>
      </g>
      <!-- Motifs -->
      <g :transform="`translate(${-pullInDistance},0)`">
        <!-- Sources -->
        <g>
          <rect
            v-for="s in themesMotifsLayout.sourceGroups"
            :key="`theme-${s.key}`"
            :x="s.x0"
            :width="Math.abs(s.x1 - s.x0)"
            :y="s.y0"
            :height="s.y1 - s.y0"
            :fill="color(s.key)"
            :fill-opacity="getFillOpacity({ theme: s.key })"
          ></rect>
        </g>
        <!-- Links -->
        <g>
          <path
            v-for="l in themesMotifsLayout.links"
            :key="`theme-${l.source.key}-motif-${l.target.key}`"
            :d="ribbon(l)"
            :fill="color(l.source.key)"
            :fill-opacity="
              getFillOpacity({ theme: l.source.key, motif: l.target.key }) * 0.3
            "
          ></path>
        </g>
        <!-- Targets -->
        <g>
          <g
            v-for="t in themesMotifsLayout.targetGroups"
            :key="`motif-${t.key}`"
            :transform="`translate(0,${(t.y0 + t.y1) / 2})`"
          >
            <rect
              v-for="d in t.stacked"
              :key="`theme-${d.key}`"
              :fill="color(d.key)"
              :y="-d.bandwidth / 2"
              :height="d.bandwidth"
              :x="Math.min(d[0], d[1])"
              :width="Math.abs(d[0] - d[1])"
              :fill-opacity="getFillOpacity({ theme: d.key, motif: t.key })"
              style="cursor: pointer"
              @mouseenter="onMouseEnterThemeMotif(d, t)"
              @mouseleave="onMouseLeaveThemeMotif(d, t)"
              @click="onMouseClickThemeMotif(d, t)"
            ></rect>
          </g>
        </g>
        <g>
          <g
            v-for="t in themesMotifsLayout.targetGroups"
            :key="`motif-${t.key}`"
          >
            <polyline
              :points="getPolylinePoints(t)"
              fill="none"
              stroke="currentColor"
              :stroke-opacity="
                getFillOpacity({
                  motif: t.key
                })
              "
            ></polyline>
            <text
              dy="0.31em"
              fill="currentColor"
              :transform="`translate(${getlabelPoint(t)})`"
              :fill-opacity="
                getFillOpacity({
                  motif: t.key
                })
              "
              style="cursor: pointer"
              @mouseenter="onMouseEnterMotif(t)"
              @mouseleave="onMouseLeaveMotif(t)"
              @click="onMouseClickMotif(t)"
            >
              {{ t.key }}
            </text>
          </g>
        </g>
      </g>
      <!-- Themes -->
      <g>
        <text
          v-for="(s, i) in Array.from(sankeyThemesSet)"
          :key="s"
          :fill="color(s)"
          dy="0.31em"
          text-anchor="middle"
          :y="getThemeY(i, sankeyThemesSet.size)"
          :fill-opacity="getFillOpacity({ theme: s })"
          style="cursor: pointer"
          @mouseenter="onMouseEnterTheme(s)"
          @mouseleave="onMouseLeaveTheme(s)"
          @click="onClickTheme(s)"
        >
          {{ s }}
        </text>
      </g>
    </svg>
  </div>
  <div class="pa-4" v-else>
    {{ $t("sankey.pickAtLeastOneThemeWord") }}
  </div>
</template>

<script>
import ThresholdControls from "./ThresholdControls.vue";
import PickedWordsControls from "./PickedWordsControls.vue";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import rectangularSankey from "@/utils/rectangular-sankey-bar";
import ribbon from "@/utils/rectangular-ribbon";
import { INTERACTION_CATEGORIES, INTERACTION_ACTIONS } from "@/store";
import { min, max } from "d3-array";
export default {
  components: {
    ThresholdControls,
    PickedWordsControls
  },
  data: () => ({
    width: 1200,
    pullInDistance: 0,
    sourceInnerRadius: 120,
    sourceOuterRadius: 130,
    targetInnerRadius: 300,
    targetOuterRadius: 400,
    targetLabelRadius: 420,
    sourceTotalHeight: 400,
    targetHeight: 16,
    padding: 4,
    themeLabelStep: 30
  }),
  computed: {
    ...mapState([
      "highlightedThemes",
      "highlightedMotifs",
      "highlightedMoods",
      "highlightedThemeToMoodFlows",
      "highlightedThemeToMotifFlows",
      "highlightedPoems",
      "highlightFreezed",
      "interactingCategory",
      "sankeyThemeToMoodFlows",
      "sankeyThemeToMotifFlows",
      "sankeyMoodsSet",
      "sankeyMotifsSet"
    ]),
    ...mapGetters(["isHighlighting", "color", "sankeyThemesSet"]),
    themesMoodsLayout() {
      return rectangularSankey()
        .directionLeftToRight(false)
        .padding(this.padding)
        .sourceTotalHeight(this.sourceTotalHeight)
        .targetHeight(this.targetHeight)
        .sourceInnerRadius(this.sourceInnerRadius)
        .sourceOuterRadius(this.sourceOuterRadius)
        .targetInnerRadius(this.targetInnerRadius)
        .targetOuterRadius(this.targetOuterRadius)
        .sourcesOrder([...this.sankeyThemesSet])(
        this.sankeyThemeToMoodFlows,
        this.sankeyMoodsSet
      );
    },
    themesMotifsLayout() {
      return rectangularSankey()
        .directionLeftToRight(true)
        .padding(this.padding)
        .sourceTotalHeight(this.sourceTotalHeight)
        .targetHeight(this.targetHeight)
        .sourceInnerRadius(this.sourceInnerRadius)
        .sourceOuterRadius(this.sourceOuterRadius)
        .targetInnerRadius(this.targetInnerRadius)
        .targetOuterRadius(this.targetOuterRadius)
        .sourcesOrder([...this.sankeyThemesSet])(
        this.sankeyThemeToMotifFlows,
        this.sankeyMotifsSet
      );
    },
    height() {
      const groups = [
        this.themesMoodsLayout.sourceGroups,
        this.themesMoodsLayout.targetGroups,
        this.themesMotifsLayout.sourceGroups,
        this.themesMotifsLayout.targetGroups
      ];
      const lasts = groups.map(g => {
        if (g.length) return g[g.length - 1];
        return { y1: 0 };
      });
      const firsts = groups.map(g => {
        if (g.length) return g[0];
        return { y0: 0 };
      });
      return (
        max([
          max(lasts, d => d.y1) - min(firsts, d => d.y0),
          this.sourceTotalHeight
        ]) +
        this.targetHeight * 2
      );
    }
  },
  created() {
    this.ribbon = ribbon();
  },
  methods: {
    ...mapMutations(["updateHighlightFreezed"]),
    ...mapActions(["updateHighlight"]),
    getPolylinePoints(d) {
      const sign = d.x1 < 0 ? -1 : 1;
      const p0 = [d.x1, (d.y0 + d.y1) / 2];
      const p1 = [p0[0] + sign * 12, p0[1]];
      return [p0, p1].join(" ");
    },
    getlabelPoint(d) {
      const sign = d.x1 < 0 ? -1 : 1;
      return [d.x1 + sign * 16, (d.y0 + d.y1) / 2];
    },
    getThemeY(i, n) {
      return (i - (n - 1) / 2) * this.themeLabelStep;
    },
    getFillOpacity(keys) {
      if (!this.isHighlighting) return 1;
      if (keys.theme && keys.mood) {
        return this.highlightedThemeToMoodFlows.some(
          f => f.source === keys.theme && f.target === keys.mood
        )
          ? 1
          : 0.15;
      } else if (keys.theme && keys.motif) {
        return this.highlightedThemeToMotifFlows.some(
          f => f.source === keys.theme && f.target === keys.motif
        )
          ? 1
          : 0.15;
      } else if (keys.theme) {
        return this.highlightedThemes.includes(keys.theme) ? 1 : 0.3;
      } else if (keys.mood) {
        return this.highlightedMoods.includes(keys.mood) ? 1 : 0.3;
      } else if (keys.motif) {
        return this.highlightedMotifs.includes(keys.motif) ? 1 : 0.3;
      }
      return 1;
    },
    onMouseEnterTheme(theme) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME,
        value: theme,
        action: INTERACTION_ACTIONS.HOVER_ADD
      });
    },
    onMouseLeaveTheme(theme) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME,
        value: theme,
        action: INTERACTION_ACTIONS.HOVER_REMOVE
      });
    },
    onClickTheme(theme) {
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME,
        value: theme,
        action: INTERACTION_ACTIONS.CLICK_TOGGLE
      });
      this.updateHighlightFreezed(this.highlightedThemes.length ? true : false);
    },
    onMouseEnterMood(t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_MOOD,
        value: t.key,
        action: INTERACTION_ACTIONS.HOVER_ADD
      });
    },
    onMouseLeaveMood(t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_MOOD,
        value: t.key,
        action: INTERACTION_ACTIONS.HOVER_REMOVE
      });
    },
    onMouseClickMood(t) {
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_MOOD,
        value: t.key,
        action: INTERACTION_ACTIONS.CLICK_TOGGLE
      });
      this.updateHighlightFreezed(this.highlightedMoods.length ? true : false);
    },
    onMouseEnterThemeMood(d, t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME_MOOD,
        value: [d.key, t.key],
        action: INTERACTION_ACTIONS.HOVER_ADD
      });
    },
    onMouseLeaveThemeMood(d, t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME_MOOD,
        value: [d.key, t.key],
        action: INTERACTION_ACTIONS.HOVER_REMOVE
      });
    },
    onMouseClickThemeMood(d, t) {
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME_MOOD,
        value: [d.key, t.key],
        action: INTERACTION_ACTIONS.CLICK_TOGGLE
      });
      this.updateHighlightFreezed(this.highlightedMoods.length ? true : false);
    },
    onMouseEnterMotif(t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_MOTIF,
        value: t.key,
        action: INTERACTION_ACTIONS.HOVER_ADD
      });
    },
    onMouseLeaveMotif(t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_MOTIF,
        value: t.key,
        action: INTERACTION_ACTIONS.HOVER_REMOVE
      });
    },
    onMouseClickMotif(t) {
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_MOTIF,
        value: t.key,
        action: INTERACTION_ACTIONS.CLICK_TOGGLE
      });
      this.updateHighlightFreezed(this.highlightedMotifs.length ? true : false);
    },
    onMouseEnterThemeMotif(d, t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME_MOTIF,
        value: [d.key, t.key],
        action: INTERACTION_ACTIONS.HOVER_ADD
      });
    },
    onMouseLeaveThemeMotif(d, t) {
      if (this.highlightFreezed) return;
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME_MOTIF,
        value: [d.key, t.key],
        action: INTERACTION_ACTIONS.HOVER_REMOVE
      });
    },
    onMouseClickThemeMotif(d, t) {
      this.updateHighlight({
        category: INTERACTION_CATEGORIES.SANKEY_THEME_MOTIF,
        value: [d.key, t.key],
        action: INTERACTION_ACTIONS.CLICK_TOGGLE
      });
      this.updateHighlightFreezed(this.highlightedMotifs.length ? true : false);
    }
  }
};
</script>

<style scoped>
svg {
  display: block;
  font-size: 16px;
  color: #616161;
}
</style>
